import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LanguageSelector from '../../components/feature/LanguageSelector';
import { BookingCalendar } from '../../components/feature/BookingCalendar';
import VoiceTranslationWidget from '../../components/feature/VoiceTranslationWidget';
import ChatTranslationPanel from '../../components/feature/ChatTranslationPanel';
import LogoBrand from '../../components/feature/LogoBrand';

export default function HomePage() {
  const [formData, setFormData] = useState({
    importerOfRecord: '',
    firstName: '',
    lastName: '',
    telephone: '',
    email: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showStickyHeader, setShowStickyHeader] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyHeader(window.scrollY > 120);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Inject Google Translate script
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    }

    (window as unknown as Record<string, unknown>)['googleTranslateElementInit'] = () => {
      const GT = (window as unknown as Record<string, unknown>)['google'] as {
        translate: { TranslateElement: new (opts: object, id: string) => void };
      };
      if (GT?.translate?.TranslateElement) {
        new GT.translate.TranslateElement(
          { pageLanguage: 'en', autoDisplay: false },
          'google_translate_element'
        );
      }
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setSubmitStatus('error');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      formBody.append('importerOfRecord', formData.importerOfRecord);
      formBody.append('firstName', formData.firstName);
      formBody.append('lastName', formData.lastName);
      formBody.append('telephone', formData.telephone);
      formBody.append('email', formData.email);
      formBody.append('description', formData.description);
      const response = await fetch('https://readdy.ai/api/form/d6duggrv53dtlm7j8rr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ importerOfRecord: '', firstName: '', lastName: '', telephone: '', email: '', description: '' });
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <style>{`
        @keyframes flashText {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .flash-text { animation: flashText 1.2s ease-in-out infinite; }
        @keyframes pulseGlow {
          0%, 100% {
            text-shadow: 
              -2px -2px 0 #000,
              2px -2px 0 #000,
              -2px 2px 0 #000,
              2px 2px 0 #000,
              0 0 4px rgba(255,26,26,0.7);
            transform: scale(1);
          }
          50% {
            text-shadow: 
              -2px -2px 0 #000,
              2px -2px 0 #000,
              -2px 2px 0 #000,
              2px 2px 0 #000,
              0 0 8px rgba(255,26,26,0.9);
            transform: scale(1.03);
          }
        }
        .pulse-glow {
          animation: pulseGlow 1.4s ease-in-out infinite;
          color: #FF1A1A;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          font-synthesis: none;
          text-rendering: optimizeLegibility;
        }
        @keyframes badgePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(253,185,19,0.7); }
          50% { box-shadow: 0 0 0 8px rgba(253,185,19,0); }
        }
        .badge-pulse { animation: badgePulse 1.6s ease-in-out infinite; }
        @keyframes mobileBtnPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,26,26,0.6); }
          50% { box-shadow: 0 0 0 10px rgba(255,26,26,0); }
        }
        .mobile-btn-pulse { animation: mobileBtnPulse 1.5s ease-in-out infinite; }
        @keyframes sidebarBtnPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,26,26,0.7), 0 4px 24px rgba(255,26,26,0.3); }
          50% { box-shadow: 0 0 0 8px rgba(255,26,26,0), 0 4px 32px rgba(255,26,26,0.5); }
        }
        .sidebar-btn-pulse { animation: sidebarBtnPulse 1.5s ease-in-out infinite; }
        @keyframes sidebarGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(255,26,26,0.2), inset 0 0 20px rgba(0,0,0,0.3); }
          50% { box-shadow: 0 0 35px rgba(255,26,26,0.4), inset 0 0 20px rgba(0,0,0,0.3); }
        }
        .sidebar-glow { animation: sidebarGlow 2.5s ease-in-out infinite; }
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-scroll { animation: tickerScroll 55s linear infinite; }
        .ticker-scroll:hover { animation-play-state: paused; }
        @keyframes borderPulse {
          0%, 100% { border-color: rgba(255,26,26,0.6); }
          50% { border-color: rgba(253,185,19,0.9); }
        }
        .border-pulse { animation: borderPulse 2s ease-in-out infinite; }
        @keyframes headerSlideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .header-slide-down { animation: headerSlideDown 0.3s ease-out forwards; }
        @keyframes headerPulse {
          0%, 100% { box-shadow: 0 2px 20px rgba(255,26,26,0.3); }
          50% { box-shadow: 0 2px 30px rgba(255,26,26,0.6); }
        }
        .header-pulse { animation: headerPulse 1.8s ease-in-out infinite; }
        .goog-te-banner-frame { display: none !important; }
        body { top: 0 !important; }
        .skiptranslate { display: none !important; }
      `}</style>

      {/* ── ALWAYS-VISIBLE FIXED TRANSLATE BUTTON ── */}
      <div className="fixed top-4 right-4 z-[90]">
        <LanguageSelector prominent />
      </div>

      {/* ── STICKY SCROLL HEADER ── */}
      {showStickyHeader && (
        <div className="fixed top-0 left-0 right-0 z-[100] header-slide-down header-pulse">
          <div className="bg-black/95 backdrop-blur-md border-b-2 border-[#FF1A1A]/80 px-4 py-2.5 flex items-center justify-between gap-3">
            {/* Left: Logo */}
            <div className="hidden sm:flex flex-shrink-0">
              <LogoBrand size="sm" variant="light" />
            </div>
            {/* Center: Urgency badge + Book button */}
            <div className="flex items-center gap-2 flex-1 justify-center sm:justify-start">
              <span className="bg-[#FF1A1A] text-white text-[10px] font-black px-2.5 py-1 rounded-full tracking-widest uppercase whitespace-nowrap flash-text">
                ⚠ ACT NOW
              </span>
              <button
                onClick={() => {
                  const bookingSection = document.getElementById('booking');
                  if (bookingSection) {
                    bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className="hidden md:flex items-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/60 text-[#FDB913] font-black rounded-lg px-3 py-2 transition-all duration-200 whitespace-nowrap cursor-pointer text-xs"
              >
                <i className="ri-calendar-check-line text-sm"></i>
                <span>Book Appointment</span>
              </button>
              <span className="text-white/70 text-xs font-semibold hidden lg:block whitespace-nowrap">
                Free Consultation — No Obligation
              </span>
            </div>
            {/* Right: Language + Client Login + Call button */}
            <div className="flex items-center gap-2">
              <LanguageSelector compact />
              <Link
                to="/login"
                className="flex items-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/60 text-[#FDB913] font-black rounded-lg px-3 py-2 transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 text-xs"
              >
                <i className="ri-lock-line text-sm"></i>
                <span className="hidden sm:inline">Client Login</span>
              </Link>
              <a
                href="tel:+15093960741"
                className="flex items-center gap-2 bg-[#FF1A1A] hover:bg-[#cc0000] text-white font-black rounded-lg px-4 py-2 transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 mobile-btn-pulse"
              >
                <i className="ri-phone-fill text-5xl lg:text-6xl pulse-glow"></i>
                <span className="text-sm font-black hidden xs:block sm:block">1-(509) 396-0741</span>
                <span className="text-sm font-black sm:hidden">CALL NOW</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Background */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/b17cb914537ead23739605c1de59fc16.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col px-6 py-12 pb-24 lg:pb-12">
          <div className="max-w-7xl w-full mx-auto mt-6">

            {/* ── FULL-WIDTH TOP CALL NOW BANNER ── */}
            <div className="w-full flex flex-col items-center mb-4">
              <div className="inline-block bg-[#FDB913] text-black text-lg font-black px-10 py-2.5 rounded-full mb-2 tracking-widest uppercase badge-pulse">
                ✦ FREE CONSULTATION ✦
              </div>
              <a
                href="tel:+15093960741"
                className="flex flex-col items-center cursor-pointer hover:opacity-90 transition-opacity w-full"
              >
                <div
                  className="text-7xl lg:text-8xl tracking-tight pulse-glow text-center"
                  style={{
                    fontFamily: "'Arial Black', 'Arial', sans-serif",
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility',
                    fontWeight: 900,
                  } as React.CSSProperties}
                >
                  CALL NOW
                </div>
                <div className="flex items-center justify-center gap-3 mt-1 w-full">
                  <i className="ri-phone-fill text-5xl lg:text-6xl pulse-glow"></i>
                  <div
                    className="text-5xl lg:text-7xl tracking-tight pulse-glow"
                    style={{
                      fontFamily: "'Arial Black', 'Arial', sans-serif",
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      textRendering: 'optimizeLegibility',
                      fontWeight: 900,
                    } as React.CSSProperties}
                  >
                    1-(509) 396-0741
                  </div>
                </div>
                <div
                  className="text-white text-base font-black mt-1 tracking-widest uppercase"
                  style={{ fontWeight: 900 } as React.CSSProperties}
                >
                  TAP TO CALL — AVAILABLE NOW
                </div>
              </a>
            </div>

            {/* ── SCROLLING NEWS TICKER ── */}
            <div className="w-full mb-3 overflow-hidden bg-black/80 border-y-2 border-[#FF1A1A]/80 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-[#FF1A1A] text-white text-xs font-black px-4 py-2.5 tracking-widest uppercase whitespace-nowrap z-10">
                  ⚡ BREAKING
                </div>
                <div className="flex-1 overflow-hidden relative py-2.5">
                  <div className="ticker-scroll whitespace-nowrap inline-block">
                    <span className="text-[#FDB913] text-sm font-black tracking-wide mx-6">
                      ⚠ URGENT: Failure to File within the Statute of Limitations will Forever Bar Your Tariff Refund Filing Deadline — DO NOT MISS YOUR WINDOW
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FF1A1A] text-sm font-black mx-6">
                      🚨 US Court of International Trade Stay Order in Effect for New IEEPA Tariff Cases until Further Notice...
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      💰 Tariff overpayments can total HUNDREDS OF THOUSANDS of dollars — Recover what is yours
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-white text-sm font-semibold mx-6">
                      ⚖ Attorney Bruce Thabit — Licensed in US Court of International Trade + 11 Federal Circuit Courts — Standing By NOW
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      ✅ FREE CONSULTATION — No obligation — Importers of Record must act before the window closes
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FF1A1A] text-sm font-black mx-6">
                      ⚖ The US Supreme Court judgment in Learning Resources vs. Trump opinion rules IEEPA-based tariffs unconstitutional decided February 2026
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      ⚖ March 4, 2026: CIT in Atmus Filtration, Inc. v. United States ordered all importers of record whose entries were subject to IEEPA duties may benefit from US Supreme Court decision that IEEPA-based tariffs are unconstitutional — indicating tariff refunds
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      📋 US Court of Appeals for the Federal Circuit issues Order dated March 2, 2026 in V.O.S. Selections, Inc. v. Trump — V.O.S. Selections, Inc.&apos;s motion for immediate issuance of the mandates in these appeals is GRANTED — denying Dept of Justice&apos;s request to delay tariff refunds
                    </span>
                    {/* Duplicate content for seamless loop */}
                    <span className="text-[#FDB913] text-sm font-black tracking-wide mx-6">
                      ⚠ URGENT: Failure to File within the Statute of Limitations will Forever Bar Your Tariff Refund Filing Deadline — DO NOT MISS YOUR WINDOW
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FF1A1A] text-sm font-black mx-6">
                      🚨 US Court of International Trade Stay Order in Effect for New IEEPA Tariff Cases until Further Notice...
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      💰 Tariff overpayments can total HUNDREDS OF THOUSANDS of dollars — Recover what is yours
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-white text-sm font-semibold mx-6">
                      ⚖ Attorney Bruce Thabit — Licensed in US Court of International Trade + 11 Federal Circuit Courts — Standing By NOW
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      ✅ FREE CONSULTATION — No obligation — Importers of Record must act before the window closes
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FF1A1A] text-sm font-black mx-6">
                      ⚖ The US Supreme Court judgment in Learning Resources vs. Trump opinion rules IEEPA-based tariffs unconstitutional decided February 2026
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      ⚖ March 4, 2026: CIT in Atmus Filtration, Inc. v. United States ordered all importers of record whose entries were subject to IEEPA duties may benefit from US Supreme Court decision that IEEPA-based tariffs are unconstitutional — indicating tariff refunds
                    </span>
                    <span className="text-white/40 mx-2">|</span>
                    <span className="text-[#FDB913] text-sm font-black mx-6">
                      📋 US Court of Appeals for the Federal Circuit issues Order dated March 2, 2026 in V.O.S. Selections, Inc. v. Trump — V.O.S. Selections, Inc.&apos;s motion for immediate issuance of the mandates in these appeals is GRANTED — denying Dept of Justice&apos;s request to delay tariff refunds
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── TRUST BADGE BAR ── */}
            <div className="w-full mb-8 bg-gradient-to-r from-[#FDB913]/20 via-[#FDB913]/30 to-[#FDB913]/20 border border-[#FDB913]/60 rounded-xl px-6 py-4">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
                <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                  <i className="ri-government-fill text-[#FDB913] text-3xl"></i>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                  <span className="text-[#FDB913] text-sm font-black tracking-widest uppercase whitespace-nowrap">
                    ⚖ U.S. COURT OF INTERNATIONAL TRADE
                  </span>
                  <span className="hidden sm:block text-[#FDB913]/50 text-lg font-black">—</span>
                  <span className="text-white text-sm font-bold tracking-wide text-center leading-snug">
                    A <strong className="text-[#FDB913]">NATIONAL BAR</strong> — NOT LIMITED TO ANY PARTICULAR STATE LICENSE
                  </span>
                  <span className="hidden sm:block text-[#FDB913]/50 text-lg font-black">—</span>
                  <span className="text-white/80 text-xs font-semibold tracking-wide whitespace-nowrap">
                    Attorney Thabit Licensed Nationally to File Lawsuits in the U.S. CIT
                  </span>
                </div>
                <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                  <i className="ri-shield-star-fill text-[#FDB913] text-3xl"></i>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-start">
              {/* Left Side */}
              <div className="text-white space-y-6 max-w-2xl lg:w-1/2 text-center flex flex-col items-center">
                <div className="space-y-4 w-full">
                  <p className="text-2xl lg:text-3xl font-bold tracking-wide text-white whitespace-nowrap">
                    SERENITY LAW{' '}
                    <span className="text-lg lg:text-xl font-bold">(Professional Limited Liability Company)</span>
                  </p>
                  <p className="text-2xl lg:text-3xl font-bold tracking-wide text-white">
                    Director Bruce Thabit, Esq.
                  </p>
                  <p className="text-xl lg:text-2xl font-black tracking-tight text-white drop-shadow-[0_2px_0px_rgba(255,26,26,0.8)] whitespace-nowrap">
                    TARIFF REFUND ATTORNEY — DON&apos;T DELAY!
                  </p>
                  <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-[#FDB913]">
                    Tariff Refund &amp; Customs Duty Recovery Services
                  </h1>
                </div>

                {/* Consultation Form */}
                <div className="bg-gradient-to-br from-[#5B8FD8]/30 to-[#4A7BC8]/30 backdrop-blur-md rounded-2xl shadow-2xl p-5 max-w-sm w-full">
                  <div className="space-y-4">
                    <div className="text-center space-y-1">
                      <h2 className="text-xl font-black text-[#FDB913] tracking-wide uppercase">
                        Request a Free Consultation
                      </h2>
                      <p className="text-white/90 text-xs">
                        Fill out the form and we&apos;ll be in touch soon
                      </p>
                      <div className="flex items-center justify-center gap-3 pt-1 pb-1">
                        <div className="flex items-center gap-1 bg-green-900/40 border border-green-500/50 rounded-full px-2 py-0.5">
                          <i className="ri-lock-fill text-green-400 text-xs"></i>
                          <span className="text-green-400 text-[9px] font-black tracking-wider uppercase whitespace-nowrap">256-bit SSL</span>
                        </div>
                        <div className="flex items-center gap-1 bg-[#FDB913]/10 border border-[#FDB913]/40 rounded-full px-2 py-0.5">
                          <i className="ri-scales-3-line text-[#FDB913] text-xs"></i>
                          <span className="text-[#FDB913] text-[9px] font-black tracking-wider uppercase whitespace-nowrap">Confidential</span>
                        </div>
                      </div>
                    </div>
                    <form
                      id="consultation-form"
                      data-readdy-form
                      onSubmit={handleSubmit}
                      className="space-y-3"
                    >
                      <div>
                        <label className="block text-white text-xs font-medium mb-1">Importer of Record</label>
                        <input type="text" name="importerOfRecord" value={formData.importerOfRecord} onChange={handleInputChange} placeholder="Company / Importer Name" className="w-full px-3 py-2 rounded-md bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-white text-xs font-medium mb-1">First Name *</label>
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="John" required className="w-full px-3 py-2 rounded-md bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-sm" />
                        </div>
                        <div>
                          <label className="block text-white text-xs font-medium mb-1">Last Name *</label>
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Doe" required className="w-full px-3 py-2 rounded-md bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-white text-xs font-medium mb-1">Telephone</label>
                        <input type="tel" name="telephone" value={formData.telephone} onChange={handleInputChange} placeholder="(555) 123-4567" className="w-full px-3 py-2 rounded-md bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-sm" />
                      </div>
                      <div>
                        <label className="block text-white text-xs font-medium mb-1">Email *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" required className="w-full px-3 py-2 rounded-md bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-sm" />
                      </div>
                      <div>
                        <label className="block text-white text-xs font-medium mb-1">Description</label>
                        <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Tell us about your tariff refund inquiry..." maxLength={500} rows={3} className="w-full px-3 py-2 rounded-md bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all resize-none text-sm" />
                        <p className="text-white/70 text-xs mt-1">{formData.description.length}/500 characters</p>
                      </div>
                      <button type="submit" disabled={isSubmitting} className="w-full bg-white hover:bg-gray-100 text-[#4A7BC8] font-bold text-sm py-2.5 rounded-md transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                        <i className="ri-send-plane-fill"></i>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                      </button>
                      {submitStatus === 'success' && (
                        <div className="bg-green-500/20 border border-green-500/50 text-white px-3 py-2 rounded-md text-center text-sm">
                          Thank you! We&apos;ll be in touch soon.
                        </div>
                      )}
                      {submitStatus === 'error' && (
                        <div className="bg-red-500/20 border border-red-500/50 text-white px-3 py-2 rounded-md text-center text-sm">
                          Something went wrong. Please try again.
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>

              {/* Right Side */}
              <div className="lg:w-1/2 flex flex-col gap-6">
                <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-8 lg:p-10">
                  <div className="space-y-4">
                    <p className="text-[#FDB913] text-2xl lg:text-3xl font-black leading-tight tracking-wide drop-shadow-lg whitespace-nowrap">
                      IMPORTERS OF RECORD ACT NOW!!!
                    </p>
                    <p className="text-[#FF1A1A] text-lg lg:text-xl font-black leading-tight tracking-wide uppercase">
                      ⚠ URGENT! THE CLOCK IS TICKING...
                    </p>
                    <p className="text-[#FDB913] text-xl lg:text-2xl font-black leading-tight tracking-wide uppercase">
                      ON-BOARDING CLIENTS HAPPENING HERE...
                    </p>
                    <p className="text-white text-lg lg:text-xl font-semibold leading-tight">
                      WE ARE ON HIGH ALERT &amp; STANDING BY.
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl overflow-hidden border-2 border-[#FF1A1A]/70 sidebar-glow">
                  <div className="bg-[#FF1A1A] px-5 py-3 text-center">
                    <p className="text-white text-sm font-black tracking-widest uppercase leading-tight">
                      ⚠ TARIFF REFUNDS APPROACHING — ACT IMMEDIATELY
                    </p>
                  </div>
                  <div className="bg-black/90 backdrop-blur-md flex flex-col items-center gap-5 px-8 py-7">
                    <div className="bg-[#FDB913] text-black text-sm font-black px-6 py-2 rounded-full tracking-widest uppercase badge-pulse whitespace-nowrap">
                      ✦ FREE CONSULTATION — NO OBLIGATION ✦
                    </div>
                    <div
                      className="text-5xl font-black tracking-tight pulse-glow text-center leading-none"
                      style={{
                        fontFamily: "'Arial Black', 'Arial', sans-serif",
                        WebkitFontSmoothing: 'antialiased',
                      } as React.CSSProperties}
                    >
                      CALL NOW
                    </div>
                    <a
                      href="tel:+15093960741"
                      className="sidebar-btn-pulse w-full flex items-center justify-center gap-4 bg-[#FF1A1A] hover:bg-[#cc0000] text-white font-black rounded-xl py-5 px-6 transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-phone-fill text-4xl"></i>
                      <span
                        className="text-2xl font-black leading-tight text-center whitespace-nowrap"
                        style={{ fontFamily: "'Arial Black', 'Arial', sans-serif" } as React.CSSProperties}
                      >
                        1-(509) 396-0741
                      </span>
                    </a>
                    <div className="w-full border-t border-white/20"></div>
                    <p className="text-white/60 text-sm font-bold tracking-widest uppercase text-center leading-tight">
                      AVAILABLE NOW · NO OBLIGATION
                    </p>
                  </div>
                  <div className="bg-[#FDB913] px-5 py-3 text-center">
                    <p className="text-black text-sm font-black tracking-widest uppercase leading-tight">
                      DON&apos;T DELAY — ACT NOW — EVERY DAY COUNTS
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Act Now Section */}
        <section className="relative z-10 bg-black/80 backdrop-blur-sm py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#FDB913]/20 border border-[#FDB913]/60 rounded-full px-5 py-2 mb-4">
                <i className="ri-error-warning-fill text-[#FF1A1A] text-lg"></i>
                <span className="text-[#FF1A1A] text-sm font-black tracking-widest uppercase">Critical Legal Alert</span>
                <i className="ri-error-warning-fill text-[#FF1A1A] text-lg"></i>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tight">
                WHY ACT <span className="text-[#FDB913]">NOW?</span>
              </h2>
              <p className="text-white/80 text-lg mt-3 max-w-3xl mx-auto leading-relaxed">
                <strong className="text-[#FF1A1A]">The Law Favors No Delay</strong> — Equity Aids the Vigilant, Not Those Who Slumber on Their Rights — <strong className="text-[#FDB913]">Justice Delayed is Justice Denied</strong>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {[
                {
                  icon: 'ri-calendar-event-line',
                  color: '#FF1A1A',
                  title: 'Deadline Date Is Uncertain — Act Early',
                  desc: 'The deadline(s) for the money refunds for the recovery of tariffs paid under IEEPA are uncertain pending more guidance from CIT. See Atmus Filtration, Inc. v. United States (Ct. No. 26\u201101259).',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  color: '#FF1A1A',
                  title: 'Permanent Loss of Refund Rights',
                  desc: 'Tariff payments can amount to tens or hundreds of thousands of dollars. After the deadline — whenever courts determine it falls — the U.S. government is under no obligation to return a single cent',
                },
                {
                  icon: 'ri-building-2-line',
                  color: '#FF1A1A',
                  title: 'Competitive Disadvantage',
                  desc: 'Your competitors who file in time will recover their tariff costs, reducing their overhead and undercutting your pricing. Inaction now means a permanent structural cost disadvantage in your industry. Early filers gain a decisive financial edge.',
                },
                {
                  icon: 'ri-alert-line',
                  color: '#FF1A1A',
                  title: 'Best Practice: File Now',
                  desc: 'Given the legal uncertainty surrounding the exact deadline, the only risk-free strategy is to file immediately. Waiting for legal clarity is a gamble with your money. Attorney Bruce Thabit is standing by — a free consultation costs nothing, missing the deadline costs everything.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 cursor-default border-pulse"
                  style={{ animationDelay: `${i * 0.3}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ backgroundColor: `${item.color}22`, border: `2px solid ${item.color}` }}
                    >
                      <i className={`${item.icon} text-2xl`} style={{ color: item.color }}></i>
                    </div>
                    <div>
                      <h3 className="font-black text-lg mb-2 leading-tight" style={{ color: item.color }}>
                        {item.title}
                      </h3>
                      <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-[#FF1A1A]/20 via-black/60 to-[#FF1A1A]/20 border-2 border-[#FF1A1A]/70 rounded-2xl p-8 text-center">
              <p className="text-white text-2xl lg:text-3xl font-black tracking-wide mb-2 uppercase">
                ⚠ The Law Does Not Wait — And Neither Should You ⚠
              </p>
              <p className="text-white/80 text-base mb-6 max-w-2xl mx-auto">
                Attorney Bruce Thabit and the Serenity Law team are standing by to evaluate your case immediately.
                A free consultation costs you nothing — missing the deadline costs you everything.
              </p>
              <a
                href="tel:+15093960741"
                className="inline-flex items-center gap-3 bg-[#FF1A1A] hover:bg-[#cc0000] text-white font-black text-xl px-10 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(255,26,26,0.6)] whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-2xl"></i>
                CALL NOW — 1-(509) 396-0741
              </a>
              <p className="text-white/50 text-xs mt-3 tracking-wide">FREE CONSULTATION · NO OBLIGATION · AVAILABLE NOW</p>
            </div>
          </div>
        </section>

        {/* Schedule a Consultation Section */}
        <section id="booking" className="relative z-10 bg-gradient-to-b from-black/80 to-[#0d1520] py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#FDB913]/20 border border-[#FDB913]/60 rounded-full px-5 py-2 mb-4">
                <i className="ri-calendar-check-fill text-[#FDB913] text-lg"></i>
                <span className="text-[#FDB913] text-sm font-black tracking-widest uppercase">Book Your Free Consultation</span>
                <i className="ri-calendar-check-fill text-[#FDB913] text-lg"></i>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
                SCHEDULE A <span className="text-[#FDB913]">FREE CONSULTATION</span>
              </h2>
              <p className="text-white/80 text-lg max-w-3xl mx-auto leading-relaxed">
                Select a convenient time to speak with Attorney Bruce Thabit about your tariff refund case. <strong className="text-[#FDB913]">No obligation. Completely confidential.</strong>
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <BookingCalendar
                timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/5346f5a7-cde1-4a45-9ba5-f838ee33b4ea.f138c116e5b84601d7b97fcf67f1110b52b71e0c7959f0dc4b74eb9eb19d15cc"
                appointmentsApi="https://readdy.ai/api/public/calendar/appointments/5346f5a7-cde1-4a45-9ba5-f838ee33b4ea.f138c116e5b84601d7b97fcf67f1110b52b71e0c7959f0dc4b74eb9eb19d15cc"
                texts={{
                  stepDateTitle: 'Select Date',
                  stepTimeTitle: 'Choose Time Slot',
                  stepFormTitle: 'Confirm Your Appointment',
                  prevMonth: 'Previous Month',
                  nextMonth: 'Next Month',
                  selectTime: 'Select Time',
                  selectedDate: 'Selected Date',
                  selectedTime: 'Selected Time',
                  modifyTime: 'Modify',
                  customerName: 'Full Name',
                  customerEmail: 'Email Address',
                  customerPhone: 'Phone Number',
                  notes: 'Additional Notes (Optional)',
                  submit: 'Confirm Appointment',
                  submitting: 'Booking...',
                  successMessage: 'Appointment booked successfully! You will receive a confirmation email shortly.',
                  errorMessage: 'Failed to book appointment. Please try again or contact us directly.',
                  noSlotsAvailable: 'No time slots available for this date',
                  sun: 'Sun',
                  mon: 'Mon',
                  tue: 'Tue',
                  wed: 'Wed',
                  thu: 'Thu',
                  fri: 'Fri',
                  sat: 'Sat',
                }}
                containerStyle="bg-gradient-to-br from-white/5 to-white/[0.02] border border-[#FDB913]/30 rounded-2xl p-8 lg:p-10"
              />
            </div>

            <div className="mt-10 text-center">
              <div className="inline-flex flex-col items-center gap-3 bg-[#FF1A1A]/10 border border-[#FF1A1A]/40 rounded-xl px-6 py-4">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-[#FF1A1A] text-xl"></i>
                  <p className="text-white text-sm font-black tracking-wide">Prefer to Call Now?</p>
                </div>
                <a
                  href="tel:+15093960741"
                  className="inline-flex items-center gap-3 bg-[#FF1A1A] hover:bg-[#cc0000] text-white font-black text-lg px-8 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_25px_rgba(255,26,26,0.5)] whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-fill text-xl"></i>
                  1-(509) 396-0741
                </a>
                <p className="text-white/60 text-xs tracking-wide">Available Now · Free Consultation · No Obligation</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Serenity Law Section */}
        <section className="relative z-10 bg-gradient-to-b from-[#0d1520] to-[#111827] py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 bg-[#FDB913]/20 border border-[#FDB913]/60 rounded-full px-5 py-2 mb-4">
                <i className="ri-shield-star-fill text-[#FDB913] text-lg"></i>
                <span className="text-[#FDB913] text-sm font-black tracking-widest uppercase">Official Credentials</span>
                <i className="ri-shield-star-fill text-[#FDB913] text-lg"></i>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
                WHY CHOOSE <span className="text-[#FDB913]">SERENITY LAW?</span>
              </h2>
              <p className="text-white/70 text-lg max-w-3xl mx-auto leading-relaxed">
                Attorney Bruce Thabit brings 37 years experience with skill and knowledge who holds numerous licenses can provides tariff refund representation in the U.S. Court of International Trade no matter where you import with the goal of &ldquo;just, speedy, and inexpensive&rdquo; results.
              </p>
            </div>

            {/* Attorney Profile Card */}
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-[#FDB913]/30 rounded-2xl p-8 lg:p-10 mb-12 flex flex-col lg:flex-row gap-8 items-center lg:items-start">
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-[180px] h-[220px] rounded-2xl overflow-hidden border-2 border-[#FDB913]/70 shadow-[0_0_30px_rgba(253,185,19,0.2)]">
                  <img
                    src="https://static.readdy.ai/image/259abf77f22ac293ef2035e23bde2aae/d7935218b8302765b7875c51387808ab.jpeg"
                    alt="Bruce Thabit, Tariff Refund Attorney"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-3 text-center">
                  <p className="text-white font-black text-base tracking-wide">Bruce Thabit, Esq.</p>
                  <p className="text-[#FDB913] text-xs font-bold tracking-widest uppercase mt-0.5">Director, Serenity Law PLLC</p>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 w-full">
                  {[
                    { num: '4', label: 'State Licenses' },
                    { num: '11', label: 'Circuit Courts' },
                    { num: '9', label: 'District Courts' },
                    { num: '2', label: 'National Courts' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#FDB913]/10 border border-[#FDB913]/30 rounded-lg px-2 py-2 text-center">
                      <p className="text-[#FDB913] text-2xl font-black leading-none">{stat.num}</p>
                      <p className="text-white/60 text-[10px] font-black tracking-widest uppercase mt-0.5">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 w-full bg-gradient-to-r from-[#FF1A1A]/20 to-[#FDB913]/20 border border-[#FDB913]/50 rounded-xl px-3 py-3 text-center">
                  <p className="text-[#FDB913] text-3xl font-black leading-none">37</p>
                  <p className="text-white/70 text-[10px] font-black tracking-widest uppercase mt-0.5">Years Licensed</p>
                  <p className="text-white/50 text-[9px] tracking-wide mt-0.5">Since 1988</p>
                </div>
              </div>

              <div className="flex-1 space-y-6">
                <div>
                  <h3 className="text-2xl font-black text-white mb-3 tracking-tight">
                    <span className="text-[#FDB913]">Ready to Zealously Advocate for Your Refund.</span>
                  </h3>
                  <p className="text-white/75 text-sm leading-relaxed">
                    Attorney Bruce Thabit has established a law practice to recover tariffs imposed under IEEPA for importers of record and is positioned to file protests, pursue refunds and prosecute claims regardless of where your business is located or where your goods entered the country.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#FDB913]/15 via-[#FDB913]/10 to-transparent border-2 border-[#FDB913]/60 rounded-2xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#FDB913]/20 rounded-full border border-[#FDB913]/60 flex-shrink-0">
                      <i className="ri-vip-crown-fill text-[#FDB913] text-xl"></i>
                    </div>
                    <div>
                      <p className="text-[#FDB913] text-base font-black tracking-wide uppercase leading-tight">White Glove Service. Cotton Sock Rates.</p>
                      <p className="text-white/60 text-xs font-semibold tracking-widest uppercase mt-0.5">Why Pay Big Firm Fees?</p>
                    </div>
                  </div>
                  <p className="text-white/85 text-sm leading-relaxed">
                    <strong className="text-white">Why pay white shoe law firm legal fees</strong> when you can still have <strong className="text-[#FDB913]">white glove concierge legal representation</strong> for cotton sock rates? Serenity Law delivers the same caliber of sophisticated federal litigation and transactional counsel as the largest firms.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['No Big-Firm Overhead', 'Direct Attorney Access', 'Concierge Service'].map((tag) => (
                      <span key={tag} className="bg-[#FDB913]/20 border border-[#FDB913]/40 text-[#FDB913] text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-global-line', color: '#FDB913', title: 'National Jurisdiction', desc: 'U.S. Court of International Trade is a national bar — not limited to any state' },
                    { icon: 'ri-time-fill', color: '#FF1A1A', title: 'Standing By Now', desc: 'Available immediately for urgent deadline-driven filings' },
                    { icon: 'ri-history-line', color: '#FDB913', title: '37 Years Licensed Since 1988', desc: 'Licensed since 1988 — decades of federal litigation and transactional law experience' },
                    { icon: 'ri-scales-3-line', color: '#FDB913', title: 'Litigation & Transactional Law', desc: 'Skilled litigator prosecuting constitutional and statutory violations — and transactional counsel' },
                    { icon: 'ri-store-2-fill', color: '#FDB913', title: 'Built for Small & Mid-Size Importers', desc: 'Small to Mid-size importers — businesses who paid IEEPA tariffs and deserve the same knowledgeable, skilled legal representation as Fortune 500 companies, at rates that make economic sense for your business.' },
                    { icon: 'ri-money-dollar-circle-fill', color: '#FDB913', title: 'Free Consultation', desc: 'No-obligation case evaluation — know your refund potential before committing' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white/[0.03] border border-white/10 rounded-xl p-3 hover:bg-white/[0.06] transition-all duration-200">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg" style={{ backgroundColor: `${item.color}22` }}>
                        <i className={`${item.icon} text-lg`} style={{ color: item.color }}></i>
                      </div>
                      <div>
                        <p className="text-white text-sm font-black leading-tight">{item.title}</p>
                        <p className="text-white/55 text-xs leading-snug mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-[#FF1A1A]/10 to-[#FF1A1A]/5 border border-[#FF1A1A]/40 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#FF1A1A]/20 rounded-full border border-[#FF1A1A]/50 flex-shrink-0 mt-0.5">
                    <i className="ri-focus-3-line text-[#FF1A1A] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-[#FF1A1A] text-sm font-black tracking-wide uppercase mb-1">Our Target Client</p>
                    <p className="text-white/80 text-sm leading-relaxed">
                      Small to Mid-size importers — businesses who paid IEEPA tariffs and deserve the same knowledgeable, skilled legal representation as Fortune 500 companies, at rates that make economic sense for your business.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* License Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="bg-white/[0.04] border border-[#FDB913]/40 rounded-2xl p-6 hover:border-[#FDB913]/70 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FDB913]/20 rounded-full border border-[#FDB913]/50">
                    <i className="ri-map-pin-2-fill text-[#FDB913] text-xl"></i>
                  </div>
                  <div>
                    <p className="text-[#FDB913] text-xs font-black tracking-widest uppercase">4 States</p>
                    <p className="text-white font-black text-sm">State Licenses</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {['California', 'Hawaii', 'Maryland', 'Washington'].map((state) => (
                    <div key={state} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-[#FDB913] text-sm flex-shrink-0"></i>
                      <span className="text-white/80 text-sm font-medium">{state}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/[0.04] border border-[#FF1A1A]/40 rounded-2xl p-6 hover:border-[#FF1A1A]/70 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF1A1A]/20 rounded-full border border-[#FF1A1A]/50">
                    <i className="ri-scales-3-fill text-[#FF1A1A] text-xl"></i>
                  </div>
                  <div>
                    <p className="text-[#FF1A1A] text-xs font-black tracking-widest uppercase">11 Courts</p>
                    <p className="text-white font-black text-sm">U.S. Circuit Courts</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {['1st Circuit', '2nd Circuit', '3rd Circuit', '4th Circuit', '5th Circuit', '6th Circuit', '7th Circuit', '8th Circuit', '9th Circuit', '10th Circuit', '11th Circuit'].map((court) => (
                    <div key={court} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-[#FF1A1A] text-sm flex-shrink-0"></i>
                      <span className="text-white/80 text-sm font-medium">{court}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/[0.04] border border-white/20 rounded-2xl p-6 hover:border-white/40 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full border border-white/30">
                    <i className="ri-building-4-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs font-black tracking-widest uppercase">9 Courts</p>
                    <p className="text-white font-black text-sm">U.S. District Courts</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {['D. Haw.', 'D. Neb.', 'S.D. Cal.', 'C.D. Cal.', 'N.D. Cal.', 'E.D. Wash.', 'E.D. Okla.', 'E.D. Mo.', 'D. N.D.'].map((court) => (
                    <div key={court} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-white/50 text-sm flex-shrink-0"></i>
                      <span className="text-white/80 text-sm font-medium">{court}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/[0.04] border border-[#FDB913]/40 rounded-2xl p-6 hover:border-[#FDB913]/70 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FDB913]/20 rounded-full border border-[#FDB913]/50">
                    <i className="ri-government-fill text-[#FDB913] text-xl"></i>
                  </div>
                  <div>
                    <p className="text-[#FDB913] text-xs font-black tracking-widest uppercase">National</p>
                    <p className="text-white font-black text-sm">National Courts</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  {['U.S. Tax Court', 'U.S. Court of International Trade'].map((court) => (
                    <div key={court} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-[#FDB913] text-sm flex-shrink-0"></i>
                      <span className="text-white/80 text-sm font-medium">{court}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-[#FDB913]/10 border border-[#FDB913]/30 rounded-xl p-3">
                  <p className="text-[#FDB913] text-xs font-black tracking-wide uppercase mb-1">Key Advantage</p>
                  <p className="text-white/70 text-xs leading-relaxed">
                    The U.S. Court of International Trade is a <strong className="text-white">national bar</strong> — Attorney Thabit can represent importers regardless of your locality.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#FDB913]/10 via-[#FDB913]/20 to-[#FDB913]/10 border border-[#FDB913]/50 rounded-2xl p-8 text-center">
              <p className="text-[#FDB913] text-2xl lg:text-3xl font-black tracking-wide mb-2 uppercase">
                Ready to File Your Claim Today.
              </p>
              <p className="text-white/75 text-base mb-6 max-w-2xl mx-auto">
                No matter where your business is located, Attorney Thabit can represent you before the U.S. Court of International Trade — a national court with no geographic restrictions.
              </p>
              <a
                href="tel:+15093960741"
                className="inline-flex items-center gap-3 bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-xl px-10 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(253,185,19,0.5)] whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-2xl"></i>
                CALL NOW — 1-(509) 396-0741
              </a>
              <p className="text-white/50 text-xs mt-3 tracking-wide">FREE CONSULTATION · NO OBLIGATION · AVAILABLE NOW</p>
            </div>
          </div>
        </section>

        {/* Languages Translated Section */}
        <section className="relative z-10 bg-gradient-to-b from-[#111827] to-[#0d1520] py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#FDB913]/20 border border-[#FDB913]/60 rounded-full px-5 py-2 mb-4">
                <i className="ri-global-line text-[#FDB913] text-lg"></i>
                <span className="text-[#FDB913] text-sm font-black tracking-widest uppercase">International Importers Welcome</span>
                <i className="ri-global-line text-[#FDB913] text-lg"></i>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tight mb-3">
                LANGUAGES <span className="text-[#FDB913]">TRANSLATED</span>
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
                Our consultations and communications are available in multiple languages solely through the use of translation tools — no language barrier should stand between you and your tariff refund. <strong className="text-white">Note: Attorney only speaks English.</strong>
              </p>
            </div>

            {/* Language Badges Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-10">
              {[
                { flag: '🇺🇸', lang: 'English', native: 'English' },
                { flag: '🇪🇸', lang: 'Spanish', native: 'Español' },
                { flag: '🇨🇳', lang: 'Chinese (Simplified)', native: '中文 (简体)' },
                { flag: '🇹🇼', lang: 'Chinese (Traditional)', native: '中文 (繁體)' },
                { flag: '🇫🇷', lang: 'French', native: 'Français' },
                { flag: '🇩🇪', lang: 'German', native: 'Deutsch' },
                { flag: '🇯🇵', lang: 'Japanese', native: '日本語' },
                { flag: '🇰🇷', lang: 'Korean', native: '한국어' },
                { flag: '🇧🇷', lang: 'Portuguese', native: 'Português' },
                { flag: '🇸🇦', lang: 'Arabic', native: 'العربية' },
                { flag: '🇮🇳', lang: 'Hindi', native: 'हिन्दी' },
                { flag: '🇻🇳', lang: 'Vietnamese', native: 'Tiếng Việt' },
                { flag: '🇵🇭', lang: 'Filipino', native: 'Filipino' },
                { flag: '🇮🇹', lang: 'Italian', native: 'Italiano' },
                { flag: '🇷🇺', lang: 'Russian', native: 'Русский' },
              ].map((item) => (
                <div
                  key={item.lang}
                  className="flex items-center gap-3 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#FDB913]/40 rounded-xl px-4 py-2 transition-all duration-200 cursor-default group"
                >
                  <span className="text-2xl flex-shrink-0">{item.flag}</span>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-black leading-tight truncate group-hover:text-[#FDB913] transition-colors">{item.native}</p>
                    <p className="text-white/45 text-[10px] font-semibold tracking-wide truncate">{item.lang}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#1a2332] py-6 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/70 text-sm">© 2025 Serenity Law PLLC. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a
                href="/login"
                className="flex items-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/50 text-[#FDB913] font-bold text-sm px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-lock-line"></i>
                Client Login
              </a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white/90 text-sm transition-colors cursor-pointer whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>

          {/* Language Disclaimer */}
          <div className="max-w-7xl mx-auto mt-4 pt-4 border-t border-white/10">
            <div className="flex items-start gap-3 bg-white/[0.03] border border-[#FDB913]/20 rounded-xl px-4 py-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#FDB913]/15 rounded-full border border-[#FDB913]/30 flex-shrink-0 mt-0.5">
                <i className="ri-translate-2 text-[#FDB913] text-sm"></i>
              </div>
              <p className="text-white/55 text-xs leading-relaxed">
                <strong className="text-[#FDB913]/80">Language Disclaimer:</strong> This website and all client communications are conducted in English. Translations are provided solely through automated translation tools for informational convenience and may not be fully accurate. <strong className="text-white/70">Attorney Bruce Thabit speaks English only.</strong>
              </p>
            </div>
          </div>

          <div className="max-w-7xl mx-auto mt-6 pt-4 border-t border-white/10 flex flex-col md:flex-row gap-6 items">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-[160px] h-[220px] rounded-xl overflow-hidden border-2 border-[#FDB913]/60 shadow-lg">
                <img
                  src="https://static.readdy.ai/image/259abf77f22ac293ef2035e23bde2aae/d7935218b8302765b7875c51387808ab.jpeg"
                  alt="Bruce Thabit, Tariff Refund Attorney"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="text-white text-sm font-black tracking-wide mt-2">Bruce Thabit, Esq.</p>
            </div>

            <div className="text-white/80 text-sm leading-relaxed space-y-3 text-left flex-1">
              <div>
                <p className="text-[#FDB913] text-xs font-black tracking-widest uppercase mb-1">State Licenses</p>
                <p className="text-white/70 text-[15px] font-semibold leading-relaxed">
                  California, Hawaii, Maryland, Washington
                </p>
              </div>
              <div>
                <p className="text-[#FDB913] text-xs font-black tracking-widest uppercase mb-1">Federal Licenses</p>
                <div className="space-y-1.5">
                  <div>
                    <span className="text-white/60 text-[11px] font-bold tracking-widest uppercase">U.S. Circuit Courts</span>
                    <p className="text-white/70 text-[14px] font-medium leading-relaxed">
                      1st Cir., 2d Cir., 3d Cir., 4th Cir., 5th Cir., 6th Cir., 7th Cir., 8th Cir., 9th Cir., 10th Cir., 11th Cir.
                    </p>
                  </div>
                  <div>
                    <span className="text-white/60 text-[11px] font-bold tracking-widest uppercase">U.S. District Courts</span>
                    <p className="text-white/70 text-[14px] font-medium leading-relaxed">
                      D. Haw., D. Neb., S.D. Cal., C.D. Cal., N.D. Cal., E.D. Wash., E.D. Okla., E.D. Mo., D. N.D.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[#FDB913] text-xs font-black tracking-widest uppercase mb-1">National Licenses</p>
                <p className="text-white/70 text-[15px] font-semibold leading-relaxed">
                  U.S. Tax Court, U.S. Court of International Trade
                </p>
              </div>
            </div>
          </div>
        </footer>

        {/* Contact Information */}
        <div className="bg-[#111827] py-6 px-6">
          <div className="max-w-7xl mx-auto text-center space-y-1">
            <p className="text-white font-bold text-base">SERENITY LAW PLLC (Professional Limited Liability Company)</p>
            <p className="text-white/80 text-sm">Director Attorney Bruce Thabit</p>
            <p className="text-white/80 text-sm">1037 NE 65th St #80300</p>
            <p className="text-white/80 text-sm">Seattle, WA 98115</p>
            <p className="pt-2 text-white/90 text-sm font-medium">
              United States: Telephone VOiP:{' '}
              <a href="tel:+15093960741" className="hover:text-[#FDB913] transition-colors cursor-pointer">1-(509) 396-0741</a>
            </p>
            <p>
              <a href="mailto:hello@mail.tariffrefund.attorney" className="text-[#FDB913] hover:text-white transition-colors cursor-pointer font-medium text-sm">
                hello@mail.tariffrefund.attorney
              </a>
            </p>
            <p className="text-white/60 text-xs">Encrypted mailbox powered by ProtonMail.com</p>
            <p className="text-white/60 text-xs">
              Get free ProtonMail.com acct for end to end encrypted communications here:{' '}
              <a
                href="https://pr.tn/ref/798HY1EM"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FDB913] hover:text-white transition-colors cursor-pointer underline"
              >
                https://pr.tn/ref/798HY1EM
              </a>
            </p>
          </div>
        </div>

        {/* Voice Translation Widget */}
        <VoiceTranslationWidget />

        {/* Chat Translation Panel */}
        <ChatTranslationPanel />

        {/* Mobile Floating Client Login Button */}
        <div className="fixed top-20 right-4 z-[85] lg:hidden">
          <a
            href="/login"
            className="flex items-center gap-2 bg-[#0d1520]/95 backdrop-blur-sm border-2 border-[#FDB913]/70 text-[#FDB913] font-black text-xs px-3 py-2.5 rounded-xl shadow-lg whitespace-nowrap cursor-pointer"
            style={{ boxShadow: '0 0 12px rgba(253,185,19,0.4)' }}
          >
            <i className="ri-lock-line text-sm"></i>
            Client Login
          </a>
        </div>

        {/* Sticky Mobile CALL NOW Button */}
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
          <a
            href="tel:+15093960741"
            className="mobile-btn-pulse flex items-center justify-center gap-3 bg-[#FF1A1A] text-white font-black text-xl py-4 w-full whitespace-nowrap cursor-pointer"
            style={{
              fontFamily: "'Arial Black', 'Arial', sans-serif",
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            } as React.CSSProperties}
          >
            <i className="ri-phone-fill text-2xl"></i>
            CALL NOW — 1-(509) 396-0741
          </a>
          <div className="bg-black text-white/70 text-[10px] font-bold text-center py-1 tracking-widest uppercase">
            FREE CONSULTATION · NO OBLIGATION
          </div>
        </div>
      </div>
    </div>
  );
}
